import { Link, Stack } from '@mui/material';
import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { ResourceSummary } from '../../../common/components/ResourceSummary';
import { ResourceInfoItem } from '../../../common/components/ResourceSummaryInfo';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { TraceType } from '../../../common/enums/TraceType';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { KeyCertificatesTable } from '../../../common/keys/components/KeyCertificatesTable';
import { KeyOperationsTable } from '../../../common/keys/components/KeyOperationsTable';
import { KeyPCKS11AttributesTable } from '../../../common/keys/components/KeyPCKS11AttributesTable';
import { KeyCertificate } from '../../../common/keys/models/KeyCertificate';
import { KeyOperation } from '../../../common/keys/models/KeyOperation';
import { KeyVariant } from '../../../common/keys/services/KeyCertificatesService';
import { LocationsTable } from '../../../common/locations/components/LocationsTable';
import { Location } from '../../../common/locations/models/Location';
import { LocationVariant } from '../../../common/locations/services/LocationsService';
import { PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone } from '../../common/models/Report';
import { ReportKeyDetail } from '../models/ReportKeyDetail';
import { ReportKeysRepository } from '../services/ReportKeysRepository';

type State = BaseState & {
  loading: boolean;
};

type Props = WithRouterProps<ReportOutletContextType> & {};

class Controller extends BaseController<Props, State> {
  private readonly keyService = new ReportKeysRepository();

  private readonly keyId: number;
  private readonly reportId: number;

  private report: ReportDone | null;
  private reportKey: ReportKeyDetail | null;

  private initialCertificates: PaginationResult<KeyCertificate> | null;
  private initialOperations: PaginationResult<KeyOperation> | null;
  private initialLocations: PaginationResult<Location> | null;

  private keySummary: ResourceInfoItem[] | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ keyId: this.keyId, reportId: this.reportId } = this.parseNumericIds(['keyId', 'reportId']));

    this.report = null;
    this.reportKey = null;

    this.initialCertificates = null;
    this.initialOperations = null;
    this.initialLocations = null;

    this.keySummary = null;

    this.state = { ...super.getBaseState(), loading: true };
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.keyService.cancelAllRequests();
  }

  render() {
    const { loading } = this.state;

    const value = this.reportKey?.getValueJson() ?? null;
    const metadata = this.reportKey?.getMetadata() ?? null;
    const attributes = this.reportKey?.getPkcs11Attributes() ?? null;

    const traceType = this.report?.getTraceType() || null;

    return this.renderBase(
      <Stack gap={2}>
        <BackButton />
        <ResourceSummary
          title={t('detailsForKeyId', { keyId: this.keyId.toString() })}
          loading={loading}
          items={this.keySummary}
          value={value}
          metadataValue={metadata}
          valueTitle={t('keyValue')}
          metadataTitle={t('keyMetadata')}
        />
        {(!this.initialCertificates || this.initialCertificates.data.length > 0) && (
          <KeyCertificatesTable
            keyId={this.keyId}
            reportId={this.reportId}
            keyVariant={KeyVariant.ReportKey}
            initialData={this.initialCertificates}
            withPrefill
            parentLoading={loading}
          />
        )}
        {attributes && attributes.length > 0 && <KeyPCKS11AttributesTable rows={attributes} />}
        {(!this.initialLocations || this.initialLocations.data.length > 0) && (
          <LocationsTable variant={LocationVariant.Key} variantId={this.keyId} initialData={this.initialLocations} withPrefill parentLoading={loading} />
        )}
        {(!this.initialOperations || this.initialOperations.data.length > 0) && (
          <KeyOperationsTable traceType={traceType} keyId={this.keyId} initialData={this.initialOperations} withPrefill parentLoading={loading} />
        )}
      </Stack>
    );
  }

  private async fetchData() {
    try {
      if (!this.report) return;

      const { key, initialCertificates, initialLocations, initialOperations } = await this.keyService.getKey(this.reportId, this.keyId);
      this.reportKey = key;
      this.initialCertificates = initialCertificates;
      this.initialLocations = initialLocations;
      this.initialOperations = initialOperations;
      this.keySummary = this.createReportKeyDetails(this.report, this.reportKey);
      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e, { invalidUrl: t('nonExistingKey') });
    }
  }

  private createReportKeyDetails(report: Report, reportKey: ReportKeyDetail): ResourceInfoItem[] {
    const api = report.getTraceType();

    const items: ResourceInfoItem[] = [{ name: t('keyType'), data: reportKey.getShortDescription() }];
    if (api !== TraceType.HOST_SCANNER) {
      items.push({ name: t('source'), data: reportKey.getSource() });
      items.push({ name: t('usageSummary'), data: reportKey.getOperationSummary() });
    }
    items.push({ name: t('privateKey'), data: reportKey.getHasPrivateKey() ? t('yes') : t('no') });
    const orgKeyId = reportKey.getOrganizationKeyId()?.toString() ?? null;
    if (orgKeyId) {
      items.push({
        name: t('organizationKey'),
        data: (
          <Link
            href={UrlHelper.generatePath(PATHS.KEY, { keyId: orgKeyId })}
            aria-label={t('a11y.report.keys.organizationKeyLabel', { keyId: orgKeyId }) as string}
          >
            {orgKeyId}
          </Link>
        ),
      });
    }
    return items;
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed, ReportController will redirect to Keys
      // It would not make sense to try and load the same `keyId` on a different Report
      return;
    }

    if (currReport instanceof ReportDone) {
      // Do nothing unless the new Report is a ReportDone
      this.report = currReport;
      this.fetchData();
    }
  }
}

export const ReportKeyController = withRouter<ReportOutletContextType>(Controller);
